import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { ChevronDownIcon, ChevronUpIcon, TruckIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { courtesyContentStringAtom } from '@service/bugo/containers/MemberCourtesy/courtesy.atom';
import { getCourtesyStringFromContent } from '@service/bugo/containers/MemberCourtesy/courtesyGenerator';
import { GiftCouponShareModal } from '@service/giftStore/containers/GiftCouponShareModal/GiftCouponShareModal';
import { bugoDataAtomFamily, bugoStatusAtomFamily } from '@shared/api/bugo/bugo.atom';
import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import {
  courtesyContentListByMemberDataAtom,
  courtesyContentListByMemberStatusAtom,
} from '@shared/api/courtesyAsset/courtesyAsset.atom';
import { useCourtesyContentListByMemberHook } from '@shared/api/courtesyAsset/courtesyAsset.hook';
import { shareCoupon } from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import {
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
} from '@shared/api/giftOrder/giftOrder.atom';
import { updateGiftOrderCourtesy } from '@shared/api/giftOrder/giftOrder.controller';
import { useGiftOrderForStoreHook } from '@shared/api/giftOrder/giftOrder.hook';
import {
  GiftOrderItem,
  GiftOrderItemType,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { useParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';

// 공유 상태 색상
const getShareStatusStyle = (status: GiftCouponShareStatus) => {
  switch (status) {
    case GiftCouponShareStatus.NotShared:
      return 'bg-gray-100 text-gray-600';
    case GiftCouponShareStatus.Shared:
      return 'bg-green-100 text-green-700';
    case GiftCouponShareStatus.Viewed:
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// 공유 상태 라벨
const getShareStatusLabel = (status: GiftCouponShareStatus) => {
  switch (status) {
    case GiftCouponShareStatus.NotShared:
      return '미전달';
    case GiftCouponShareStatus.Shared:
      return '전달완료';
    case GiftCouponShareStatus.Viewed:
      return '확인됨';
    default:
      return status;
  }
};

// 컴팩트 쿠폰 행 컴포넌트
const CouponRow: FC<{
  coupon: GiftCouponForStore;
  onShare: (coupon: GiftCouponForStore) => void;
  isSharing: boolean;
}> = ({ coupon, onShare, isSharing }) => {
  const giftItem = coupon.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  // 이미지 URL
  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  // 공유 여부
  const isShared = coupon.shareStatus !== GiftCouponShareStatus.NotShared;

  // 교환 완료 여부
  const isRedeemed =
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Refunded ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Canceled;

  // 상품명
  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  return (
    <div
      className={`flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      }`}
    >
      {/* 이미지 */}
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={itemName || '쿠폰'}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      {/* 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">#{coupon.seq}</span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${getShareStatusStyle(
              coupon.shareStatus,
            )}`}
          >
            {getShareStatusLabel(coupon.shareStatus)}
          </span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">{itemName}</p>
      </div>
      {/* 공유 버튼 */}
      {!isRedeemed && (
        <button
          onClick={() => onShare(coupon)}
          disabled={isSharing}
          className={`flex-shrink-0 rounded-lg px-3 py-2 text-xs font-bold transition ${
            isShared
              ? 'bg-green-100 text-green-700'
              : 'bg-purple-600 text-white active:bg-purple-700'
          } ${isSharing ? 'opacity-50' : ''}`}
        >
          {isSharing ? '...' : isShared ? '다시' : '전달'}
        </button>
      )}
    </div>
  );
};

// 배송형 상품 행 컴포넌트
const DeliveryRow: FC<{
  item: GiftOrderItem<GiftItemForStore, Image>;
}> = ({ item }) => {
  const imageUrl = useMemo(() => {
    if (typeof item.giftItem === 'object') {
      return item.giftItem?.giftItemInfo?.image?.url || item.giftItem?.image?.url || '';
    }
    return '';
  }, [item]);

  return (
    <div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
      {/* 이미지 */}
      <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        {imageUrl ? (
          <img
            src={fileBucketUrl(imageUrl, 'w200')}
            alt={item.giftItemEmbed.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
            No Image
          </div>
        )}
      </div>

      {/* 정보 */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <TruckIcon className="h-3 w-3 text-blue-500" />
          <span className="text-[10px] font-medium text-blue-500">배송형</span>
          <span className="text-xs text-gray-400">x{item.quantity}</span>
        </div>
        <p className="mt-0.5 truncate text-sm font-medium text-gray-800">
          {item.giftItemEmbed.name}
        </p>
      </div>

      {/* 가격 */}
      <span className="flex-shrink-0 text-sm font-bold text-red-500">
        {formatPrice(item.giftItemEmbed.priceRetail * item.quantity)}원
      </span>
    </div>
  );
};

const GiftOrderCouponPage: FC = function GiftOrderCouponPage() {
  const { orderId } = useParams<{ orderId: string }>();

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const { storeRole } = useAuth();

  // 주문 조회 (coupons가 virtual로 함께 옴)
  const role = storeRole();
  useGiftOrderForStoreHook(orderId ?? '', role);
  const orderStatus = useRecoilValue(giftOrderForStoreStatusAtomFamily(orderId ?? ''));
  const order = useRecoilValue(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  // bugo 조회 (order.bugo로)
  const bugoId = order?.bugo ?? '';
  useBugoHook(bugoId, !bugoId);
  const bugoStatus = useRecoilValue(bugoStatusAtomFamily(bugoId));
  const bugo = useRecoilValue(bugoDataAtomFamily(bugoId));

  // 답례글 목록 조회 (bugo.member._id로)
  const memberId = bugo?.member?._id ?? '';
  useCourtesyContentListByMemberHook(memberId);
  const courtesyContentListStatus = useRecoilValue(courtesyContentListByMemberStatusAtom);
  const courtesyContentList = useRecoilValue(courtesyContentListByMemberDataAtom);

  // 전역 답례글 atom (다른 페이지와 공유)
  const [savedCourtesyString, setSavedCourtesyString] = useRecoilState(
    courtesyContentStringAtom,
  );
  // giftOrder 데이터 업데이트용
  const setOrderData = useSetRecoilState(giftOrderForStoreDataAtomFamily(orderId ?? ''));

  // 로컬 답례글 상태
  const [courtesyContent, setCourtesyContent] = useState('');
  const [selectedContentIndex, setSelectedContentIndex] = useState(0);
  const [isCourtesyOpen, setIsCourtesyOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  // 초기 답례글 설정 (우선순위: order.courtesyContent > savedCourtesyString > 템플릿)
  // 한 번만 초기화하고, 이후에는 사용자 수정을 허용
  useEffect(() => {
    // 이미 초기화되었으면 스킵
    if (isInitialized) return;

    // 1. order에 저장된 답례글이 있으면 사용
    if (order?.courtesyContent) {
      setCourtesyContent(order.courtesyContent);
      setOriginalContent(order.courtesyContent);
      setSavedCourtesyString(order.courtesyContent);
      setIsInitialized(true);
      return;
    }

    // 2. 전역 atom에 저장된 답례글이 있으면 사용
    if (savedCourtesyString) {
      setCourtesyContent(savedCourtesyString);
      setOriginalContent(savedCourtesyString);
      setIsInitialized(true);
      return;
    }

    // 3. bugo와 답례글 목록이 로드되면 기본 답례글 생성
    if (bugo && courtesyContentList && courtesyContentList.length > 0) {
      const defaultContentId = bugo.fevent?.courtesyContent;
      const defaultContent = defaultContentId
        ? courtesyContentList.find((c) => c._id === defaultContentId)
        : courtesyContentList[0];

      if (defaultContent) {
        const generatedContent = getCourtesyStringFromContent(defaultContent, bugo);
        setCourtesyContent(generatedContent);
        setOriginalContent(generatedContent);
        setSavedCourtesyString(generatedContent);

        const index = courtesyContentList.findIndex((c) => c._id === defaultContent._id);
        setSelectedContentIndex(index >= 0 ? index : 0);
        setIsInitialized(true);
      }
    }
  }, [
    isInitialized,
    order?.courtesyContent,
    bugo,
    courtesyContentList,
    savedCourtesyString,
    setSavedCourtesyString,
  ]);

  // 답례글 선택 핸들러
  const handleContentSelect = useCallback(
    (index: number) => {
      if (!courtesyContentList || !bugo) return;

      const selected = courtesyContentList[index];
      if (selected) {
        const generatedContent = getCourtesyStringFromContent(selected, bugo);
        setCourtesyContent(generatedContent);
        setSavedCourtesyString(generatedContent);
        setSelectedContentIndex(index);
        setIsModified(generatedContent !== originalContent);
      }
    },
    [courtesyContentList, bugo, setSavedCourtesyString, originalContent],
  );

  // 답례글 직접 수정 핸들러
  const handleContentChange = useCallback(
    (value: string) => {
      setCourtesyContent(value);
      setSavedCourtesyString(value);
      setIsModified(value !== originalContent);
    },
    [setSavedCourtesyString, originalContent],
  );

  // 답례글 저장 핸들러
  const handleSaveCourtesy = useCallback(async () => {
    if (!orderId || !courtesyContent) return;

    try {
      setIsSaving(true);
      const updatedOrder = await updateGiftOrderCourtesy(
        orderId,
        { courtesyContent },
        role,
      );
      // 로컬 상태 업데이트
      setOrderData((prev) =>
        prev ? { ...prev, courtesyContent: updatedOrder.courtesyContent } : prev,
      );
      setOriginalContent(courtesyContent);
      setIsModified(false);
      alert('답례글이 저장되었습니다.');
    } catch (error) {
      console.error('답례글 저장 실패:', error);
      alert('답례글 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [orderId, courtesyContent, role, setOrderData]);

  useEffect(() => {
    setTitle('답례품 전달');
  }, [setTitle]);

  // 쿠폰 목록 (order.coupons에서 가져옴)
  const couponList = useMemo(() => {
    return (order?.coupons ?? []) as GiftCouponForStore[];
  }, [order]);

  // 배송형 상품 분리
  const deliveryItems = useMemo(() => {
    if (!order?.orderDetails) return [];
    return order.orderDetails.filter(
      (item) => item.type === GiftOrderItemType.Delivery,
    ) as GiftOrderItem<GiftItemForStore, Image>[];
  }, [order]);

  // 공유 모달 관련 상태
  const shareModalOpenId = `gift-coupon-share-${orderId}`;
  const setShareModalOpen = useSetRecoilState(simpleOpenAtomFamily(shareModalOpenId));
  const [currentShareCoupon, setCurrentShareCoupon] = useState<GiftCouponForStore | null>(
    null,
  );

  // 공유 링크 생성
  const currentShareUrl = useMemo(() => {
    if (!currentShareCoupon) return '';
    return `${window.location.origin}/gift-store/coupon/${currentShareCoupon.code}`;
  }, [currentShareCoupon]);

  // 공유 모달 열기 핸들러
  const handleOpenShareModal = useCallback(
    (coupon: GiftCouponForStore) => {
      setCurrentShareCoupon(coupon);
      setShareModalOpen(true);
    },
    [setShareModalOpen],
  );

  // 공유 완료 핸들러 (모달에서 공유 시 호출)
  const handleShareComplete = useCallback(async () => {
    if (!currentShareCoupon) return;

    try {
      await shareCoupon(currentShareCoupon._id, role);
      // 목록 업데이트를 위해 order 데이터 갱신
      setOrderData((prev) => {
        if (!prev || !prev.coupons) return prev;
        return {
          ...prev,
          coupons: prev.coupons.map((c) =>
            c._id === currentShareCoupon._id
              ? { ...c, shareStatus: GiftCouponShareStatus.Shared }
              : c,
          ),
        };
      });
    } catch (error) {
      console.error('공유 상태 업데이트 실패:', error);
    }
  }, [currentShareCoupon, role, setOrderData]);

  // 단일 쿠폰 공유 핸들러
  const handleShare = useCallback(
    (coupon: GiftCouponForStore) => {
      // 공유 모달 열기
      handleOpenShareModal(coupon);
    },
    [handleOpenShareModal],
  );

  // 로딩 중
  if (orderStatus === 'loading' || orderStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  // 주문 없음
  if (!order) {
    return (
      <div className="center-box min-h-screen flex-col gap-4 px-4 text-center">
        <p className="text-gray-500">주문 정보를 찾을 수 없습니다.</p>
      </div>
    );
  }

  // 통계 계산
  const totalCoupons = couponList.length;
  const sharedCoupons = couponList.filter(
    (c) => c.shareStatus !== GiftCouponShareStatus.NotShared,
  ).length;
  const unsharedCoupons = totalCoupons - sharedCoupons;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-8 pt-4">
      {/* 쿠폰이 있는 경우 */}
      {couponList.length > 0 && (
        <>
          {/* 답례글 설정 영역 */}
          <div className="mx-4 mb-4 rounded-xl bg-white shadow-md">
            <button
              onClick={() => setIsCourtesyOpen(!isCourtesyOpen)}
              className="flex w-full items-center justify-between px-4 py-3"
            >
              <span className="font-bold text-gray-800">답례글 설정</span>
              {isCourtesyOpen ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              )}
            </button>

            {isCourtesyOpen && (
              <div className="border-t px-4 pb-4 pt-3">
                {/* 답례글 선택 */}
                {courtesyContentListStatus === 'success' &&
                  courtesyContentList &&
                  courtesyContentList.length > 0 && (
                    <div className="mb-3">
                      <label className="mb-1 block text-xs font-medium text-gray-600">
                        답례글 선택
                      </label>
                      <select
                        value={selectedContentIndex}
                        onChange={(e) => handleContentSelect(Number(e.target.value))}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                      >
                        {courtesyContentList.map((content, index) => (
                          <option key={content._id} value={index}>
                            {content.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                {/* 로딩 중 */}
                {(courtesyContentListStatus === 'loading' ||
                  courtesyContentListStatus === 'idle') &&
                  bugoStatus !== 'error' && (
                    <div className="mb-3 text-center text-xs text-gray-400">
                      답례글 목록을 불러오는 중...
                    </div>
                  )}

                {/* 답례글 내용 */}
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-xs font-medium text-gray-600">
                      답례글 내용
                    </label>
                    <span className="text-xs text-gray-400">편집 가능</span>
                  </div>
                  <textarea
                    value={courtesyContent}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder="답례글을 입력하세요..."
                    rows={6}
                    className="w-full resize-none rounded-lg border border-gray-200 p-3 text-sm leading-relaxed focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-xs text-gray-400">
                      전달 버튼을 누르면 위 답례글과 함께 쿠폰 링크가 공유됩니다.
                    </p>
                    <button
                      onClick={handleSaveCourtesy}
                      disabled={isSaving || !isModified}
                      className={`break-keep rounded-lg px-4 py-1.5 text-xs font-medium transition ${
                        isModified
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-gray-100 text-gray-400'
                      } ${isSaving ? 'opacity-50' : ''}`}
                    >
                      {isSaving ? '저장 중...' : isModified ? '저장' : '저장됨'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 상단 요약 카드 */}
          <div className="mx-4 rounded-xl bg-white p-4 shadow-md">
            <h2 className="text-center text-lg font-bold text-gray-800">
              답례품을 전달해 보세요
            </h2>

            {/* 전달 현황 */}
            <div className="mt-3 flex justify-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{totalCoupons}</p>
                <p className="text-xs text-gray-500">전체</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{sharedCoupons}</p>
                <p className="text-xs text-gray-500">전달</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-400">{unsharedCoupons}</p>
                <p className="text-xs text-gray-500">미전달</p>
              </div>
            </div>

            {/* 모두 전달 완료 */}
            {unsharedCoupons === 0 && totalCoupons > 0 && (
              <div className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-green-100 py-3 text-green-700">
                <CheckCircleSolidIcon className="h-5 w-5" />
                <span className="font-bold">모두 전달 완료!</span>
              </div>
            )}
          </div>

          {/* 쿠폰 목록 */}
          <div className="mt-4 px-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-700">쿠폰 목록</h3>
              <span className="text-xs text-gray-400">{totalCoupons}장</span>
            </div>
            <div className="space-y-2">
              {couponList.map((coupon) => (
                <CouponRow
                  key={coupon._id}
                  coupon={coupon}
                  onShare={handleShare}
                  isSharing={false}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* 쿠폰이 없고 배송형만 있는 경우 */}
      {couponList.length === 0 && deliveryItems.length > 0 && (
        <div className="px-4 text-center">
          <h2 className="text-xl font-bold text-gray-800">주문 상세</h2>
          <p className="mt-1 text-sm text-gray-500">배송형 상품은 직접 배송됩니다</p>
        </div>
      )}

      {/* 배송형 상품이 있는 경우 */}
      {deliveryItems.length > 0 && (
        <div className={couponList.length > 0 ? 'mt-6' : 'mt-4'}>
          <div className="mb-2 flex items-center justify-between px-4">
            <h3 className="text-sm font-bold text-gray-700">배송 상품</h3>
            <span className="text-xs text-gray-400">{deliveryItems.length}건</span>
          </div>
          <div className="space-y-2 px-4">
            {deliveryItems.map((item, index) => (
              <DeliveryRow key={`delivery-${index}`} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* 공유 모달 */}
      <GiftCouponShareModal
        openId={shareModalOpenId}
        courtesyContent={courtesyContent}
        linkUrl={currentShareUrl}
        onShare={handleShareComplete}
      />
    </div>
  );
};

export default GiftOrderCouponPage;
